import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';

import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const CreateAccount = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);

  const validateStep1 = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (!formData?.fullName || formData?.fullName?.trim()?.length === 0) {
      newErrors.fullName = 'Full name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleStep1Continue = (e) => {
    e?.preventDefault();
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateStep2()) {
      return;
    }

    setErrors(prev => ({ ...prev, submit: '' }));
    setIsLoading(true);

    try {
      const { data, error } = await signUp({
        email: formData?.email,
        password: formData?.password,
        fullName: formData?.fullName,
        role: 'worker'
      });

      if (error) {
        setErrors(prev => ({ ...prev, submit: error?.message }));
        setIsLoading(false);
        return;
      }

      // Navigate to worker homepage after successful signup
      setAccountCreated(true);
      setIsLoading(false);
    } catch (err) {
      setErrors(prev => ({ ...prev, submit: err?.message || 'An error occurred during registration' }));
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (errors?.submit) {
      setErrors(prev => ({ ...prev, submit: '' }));
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-8 md:py-12">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-2xl shadow-elevated border border-border p-6 md:p-8">
            {accountCreated ? (
              <div className="flex flex-col items-center text-center py-6 gap-4">
                <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center">
                  <Icon name="CheckCircle" size={36} className="text-success" strokeWidth={2} />
                </div>
                <h2 className="text-2xl font-bold text-foreground">Account creation successful</h2>
                <p className="text-muted-foreground">Please refresh the page and log in.</p>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="mt-2 font-medium text-primary hover:text-primary/80 transition-smooth text-sm"
                >
                  Go to Login
                </button>
              </div>
            ) : (
              <>
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => step === 1 ? navigate('/') : setStep(1)}
                className="p-2 hover:bg-muted rounded-lg transition-smooth"
                aria-label="Go back"
              >
                <Icon name="ArrowLeft" size={20} strokeWidth={2} />
              </button>
              <div>
                <h2 className="text-2xl font-bold text-foreground">
                  Create Account
                </h2>
                <p className="text-sm text-muted-foreground">
                  Step {step} of 2
                </p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex gap-2">
                <div className={`flex-1 h-2 rounded-full transition-smooth ${step >= 1 ? 'bg-primary' : 'bg-muted'}`} />
                <div className={`flex-1 h-2 rounded-full transition-smooth ${step >= 2 ? 'bg-primary' : 'bg-muted'}`} />
              </div>
            </div>

            {step === 1 ? (
              <form onSubmit={handleStep1Continue} className="space-y-4">
                <Input
                  type="email"
                  name="email"
                  label="Email Address"
                  placeholder="Enter your email"
                  value={formData?.email}
                  onChange={handleChange}
                  error={errors?.email}
                  required
                  disabled={isLoading}
                />

                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    label="Password"
                    placeholder="Create a password"
                    value={formData?.password}
                    onChange={handleChange}
                    error={errors?.password}
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-smooth"
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} strokeWidth={2} />
                  </button>
                </div>

                <div className="relative">
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    label="Confirm Password"
                    placeholder="Confirm your password"
                    value={formData?.confirmPassword}
                    onChange={handleChange}
                    error={errors?.confirmPassword}
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-smooth"
                  >
                    <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} strokeWidth={2} />
                  </button>
                </div>

                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  fullWidth
                  disabled={isLoading}
                >
                  Continue
                </Button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="text"
                  name="fullName"
                  label="Full Name"
                  placeholder="Enter your full name"
                  value={formData?.fullName}
                  onChange={handleChange}
                  error={errors?.fullName}
                  required
                  disabled={isLoading}
                />

                {errors?.submit && (
                  <div className="flex items-start gap-2 p-4 bg-error/10 border border-error/20 rounded-lg">
                    <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <p className="text-sm text-error">{errors?.submit}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  fullWidth
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>
            )}
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="font-medium text-primary hover:text-primary/80 transition-smooth"
                  disabled={isLoading}
                >
                  Sign In
                </button>
              </p>
            </div>
              </>
            )}
          </div>
        </div>
      </div>

      <footer className="py-6 px-4 border-t border-border bg-card">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              &copy; {new Date()?.getFullYear()} RenovationFlow. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <button className="text-sm text-muted-foreground hover:text-foreground transition-smooth">
                Privacy Policy
              </button>
              <button className="text-sm text-muted-foreground hover:text-foreground transition-smooth">
                Terms of Service
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CreateAccount;